import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlayJobError, type CiImageDTO, type CiJobArtifactDTO, type CiManualJobDTO, type CiPackageDTO, type CiPipelineDTO } from '@clearflow/shared';
import { api, HttpError } from '../../lib/api.ts';

// La tooltip de commit s'ouvre au survol d'une bulle : sans délai, balayer le graphe avec la souris
// déclencherait une requête par bulle traversée. Les données ne sont chargées qu'une fois le curseur
// resté ce temps sur le même commit (la section est remontée à chaque changement de sha, voir `key`
// dans GitGraph.tsx), ou tout de suite si la tooltip est épinglée.
const CI_HOVER_DELAY_MS = 400;

// Même motif que MR_REF_RE côté API (services/gitlab.ts) : un pipeline de MR porte
// `refs/merge-requests/<iid>/head`, pas le nom de la branche.
const MR_REF_RE = /^refs\/merge-requests\/(\d+)\/head$/;

function refLabel(ref: string): string {
  const iid = MR_REF_RE.exec(ref)?.[1];
  return iid ? `MR !${iid}` : ref;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
}

// Flèche vers un plateau, même gabarit que CopyIcon (13 px, trait 1,3, currentColor) : l'app n'avait
// pas d'icône de téléchargement (les exports de traçabilité sont des boutons texte).
function DownloadIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="13" height="13">
      <path d="M8 2.5v7.5M5 7.2 8 10.2l3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 12.5h10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function ArtifactRow({ artifact }: { artifact: CiJobArtifactDTO }) {
  return (
    <li>
      <div className="commit-tooltip-ci-row">
        <span>{artifact.jobName}</span>
        {artifact.size !== null && <span className="commit-tooltip-ci-size"> {formatSize(artifact.size)}</span>}
        <a
          className="commit-tooltip-copy commit-tooltip-ci-copy"
          href={`${artifact.jobUrl}/artifacts/download`}
          target="_blank"
          rel="noreferrer"
          title="Télécharger l'archive d'artefacts"
          aria-label={`Télécharger l'archive d'artefacts du job ${artifact.jobName}`}
        >
          <DownloadIcon />
        </a>
      </div>
    </li>
  );
}

// Un artefact supprimé n'a rien à télécharger : sur un commit ancien ils sont tous supprimés, et les
// lister un par un noyait la tooltip. On les replie en une ligne par pipeline (la détection reste, elle
// servira à prévenir qu'un job manuel dépendant risque d'échouer) ; le détail, avec le lien du job dans
// GitLab, s'ouvre au clic (un `title` obligerait à rester au survol d'une ligne minuscule).
function DeletedArtifacts({ artifacts }: { artifacts: CiJobArtifactDTO[] }) {
  return (
    <details className="commit-tooltip-ci-deleted">
      <summary>{artifacts.length === 1 ? '1 artefact supprimé' : `${artifacts.length} artefacts supprimés`}</summary>
      <ul>
        {artifacts.map((a) => (
          <li key={a.jobId}>
            <a href={a.jobUrl} target="_blank" rel="noreferrer" title="Ouvrir le job dans GitLab">{a.jobName}</a>
          </li>
        ))}
      </ul>
    </details>
  );
}

function PipelineBlock({ pipeline }: { pipeline: CiPipelineDTO }) {
  const available = pipeline.artifacts.filter((a) => a.status === 'available');
  const deleted = pipeline.artifacts.filter((a) => a.status === 'deleted');
  return (
    <div className="commit-tooltip-ci-pipeline">
      <div className="commit-tooltip-ci-pipeline-head">
        <a href={pipeline.webUrl} target="_blank" rel="noreferrer" title="Ouvrir le pipeline dans GitLab">#{pipeline.id}</a>
        <span>{refLabel(pipeline.ref)}</span>
        <span className="commit-tooltip-ci-status">{pipeline.status}</span>
      </div>
      {available.length > 0 && <ul>{available.map((a) => <ArtifactRow key={a.jobId} artifact={a} />)}</ul>}
      {deleted.length > 0 && <DeletedArtifacts artifacts={deleted} />}
    </div>
  );
}

// Pas d'icône « télécharger » ici : GitLab n'offre pas d'URL de téléchargement par package (seulement par
// fichier, via `package_files`), le lien mène donc à la page du package, d'où ↗ (« ouvrir dans GitLab »,
// même glyphe que les liens Jira et Confluence de la traçabilité) et non une flèche de téléchargement.
function PackageRow({ pkg }: { pkg: CiPackageDTO }) {
  return (
    <li>
      <div className="commit-tooltip-ci-row">
        <span>{pkg.name} {pkg.version}</span>
        <span className="commit-tooltip-ci-size"> {pkg.packageType}</span>
        {pkg.webUrl && (
          <a
            className="commit-tooltip-copy commit-tooltip-ci-copy"
            href={pkg.webUrl}
            target="_blank"
            rel="noreferrer"
            title="Ouvrir le package dans GitLab"
            aria-label={`Ouvrir le package ${pkg.name} ${pkg.version} dans GitLab`}
          >
            ↗
          </a>
        )}
      </div>
    </li>
  );
}

// Nom court d'une image : dernier segment du dépôt + tag. Le registre et le groupe sont communs à
// presque toutes les images d'un projet et noient ce qui les distingue ; la référence complète reste
// disponible au survol et via « Copier » (prête pour `docker pull`).
export function imageShortName(image: Pick<CiImageDTO, 'repository' | 'tag'>): string {
  const name = image.repository.split('/').filter(Boolean).pop() ?? image.repository;
  return `${name}:${image.tag}`;
}

// Même paire d'icônes que GitGraph.tsx (bouton « copier le SHA complet »), CommitContextMenu.tsx et
// SettingsModal.tsx : chaque fichier garde sa copie locale, par convention.
function CopyIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="13" height="13">
      <rect x="5" y="5" width="8" height="9" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M3.5 10.5V3.5a1 1 0 0 1 1-1H10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function CopiedIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="13" height="13">
      <path d="M3.5 8.4 L6.5 11.4 L12.5 4.6" stroke="var(--success)" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ImageRow({ image }: { image: CiImageDTO }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  const copy = () => {
    // Sans contexte sécurisé ou permission, le presse-papiers rejette : pas de « Copié » mensonger.
    navigator.clipboard.writeText(image.location).then(() => setCopied(true), () => setCopied(false));
  };
  return (
    <li>
      <div className="commit-tooltip-ci-row">
        <code className="commit-tooltip-ci-row-name" title={image.location}>{imageShortName(image)}</code>
        <span className="commit-tooltip-ci-size"> {image.matchedBy === 'tag' ? 'tag git' : 'sha'}</span>
          {image.size !== null && <span className="commit-tooltip-ci-size"> {formatSize(image.size)}</span>}
        <button
          type="button"
          className="commit-tooltip-copy commit-tooltip-ci-copy"
          onClick={copy}
          title={copied ? 'Copié !' : `Copier la référence complète : ${image.location}`}
          aria-label="Copier la référence complète de l'image"
        >
          {copied ? <CopiedIcon /> : <CopyIcon />}
        </button>
      </div>
    </li>
  );
}

// Erreur d'un `play` : la route renvoie `{ error, jobUrl }` (PlayJobError) ; tout autre échec (réseau,
// session expirée) n'a que son message. Jamais un échec muet.
function playFailureOf(err: unknown): { message: string; jobUrl: string | null } {
  if (err instanceof HttpError) {
    try {
      const parsed = PlayJobError.safeParse(JSON.parse(err.body));
      if (parsed.success) return { message: parsed.data.error, jobUrl: parsed.data.jobUrl };
    } catch { /* corps non JSON : message générique ci-dessous */ }
  }
  return { message: err instanceof Error ? err.message : 'Lancement impossible.', jobUrl: null };
}

// Avertissement indicatif, non bloquant, affiché dans la confirmation de « Lancer » (GU-378, D1) : le test D2
// a montré que GitLab lance un job manuel même quand les artefacts d'un job antérieur ont été supprimés,
// sans erreur, et que le job s'exécute alors sans eux. L'API Jobs n'expose pas `dependencies` : on ne sait
// pas de quel job celui-ci dépend, donc l'avertissement vise tous les artefacts supprimés du pipeline.
function DeletedArtifactsWarning({ pipeline }: { pipeline: CiPipelineDTO | undefined }) {
  const deleted = pipeline?.artifacts.filter((a) => a.status === 'deleted') ?? [];
  if (deleted.length === 0) return null;
  return (
    <div className="commit-tooltip-ci-warn" role="note">
      Attention : {deleted.length === 1 ? '1 artefact supprimé' : `${deleted.length} artefacts supprimés`} dans
      ce pipeline ({deleted.map((a) => a.jobName).join(', ')}). Si ce job en dépend, il peut s'exécuter sans.
    </div>
  );
}

// Jobs manuels (GU-378, tranche 3). « Lancer » épingle la tooltip (sinon elle se refermerait dès que le
// curseur la quitte pour aller confirmer) et demande confirmation en ligne : lancer un job est une
// écriture sur GitLab (publication d'un package, etc.). Les jobs hors liste blanche ne sont que comptés.
function ManualJobs({
  repoId, pipelines, onPin, onConfigure,
}: {
  repoId: string;
  pipelines: CiPipelineDTO[];
  onPin: () => void;
  onConfigure?: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const play = useMutation({
    mutationFn: (job: CiManualJobDTO) => api.playJob(repoId, job.id),
    onSettled: () => {
      setConfirmingId(null);
      // Le job passe de `manual` à `pending` : toute la section de ce dépôt est périmée (un même pipeline
      // couvre plusieurs commits).
      void queryClient.invalidateQueries({ queryKey: ['commit-ci', repoId] });
    },
  });

  const jobs = pipelines.flatMap((p) => p.manualJobs);
  const playable = jobs.filter((j) => j.playable);
  const others = jobs.filter((j) => !j.playable);
  if (jobs.length === 0 && !play.isSuccess && !play.isError) return null;

  const otherPipeline = pipelines.find((p) => p.manualJobs.some((j) => !j.playable));
  const failure = play.isError ? playFailureOf(play.error) : null;
  return (
    <div className="commit-tooltip-ci-pipeline">
      <div className="commit-tooltip-ci-group">Jobs manuels</div>
      {playable.length > 0 && (
        <ul>
          {playable.map((job) => (
            <li key={job.id} className="commit-tooltip-ci-job">
              <span className="commit-tooltip-ci-job-name">
                <a href={job.webUrl} target="_blank" rel="noreferrer" title="Ouvrir le job dans GitLab">{job.name}</a>
                <span className="commit-tooltip-ci-size"> #{job.pipelineId} · {job.stage}</span>
              </span>
              <span className="commit-tooltip-ci-job-actions">
                {confirmingId === job.id ? (
                  <span className="commit-tooltip-ci-confirm">
                    Lancer ce job ?{' '}
                    <button className="commit-tooltip-ci-play" disabled={play.isPending} onClick={() => play.mutate(job)}>
                      {play.isPending ? 'Lancement…' : 'Confirmer'}
                    </button>{' '}
                    <button className="commit-tooltip-ci-play" disabled={play.isPending} onClick={() => setConfirmingId(null)}>
                      Annuler
                    </button>
                  </span>
                ) : (
                  <button
                    className="commit-tooltip-ci-play"
                    disabled={play.isPending}
                    onClick={() => { play.reset(); onPin(); setConfirmingId(job.id); }}
                  >
                    Lancer
                  </button>
                )}
              </span>
              {confirmingId === job.id && <DeletedArtifactsWarning pipeline={pipelines.find((p) => p.id === job.pipelineId)} />}
            </li>
          ))}
        </ul>
      )}
      {others.length > 0 && (
        <div className="commit-tooltip-ci-note">
          {others.length === 1 ? '1 autre job manuel' : `${others.length} autres jobs manuels`}
          {otherPipeline && <> — <a href={otherPipeline.webUrl} target="_blank" rel="noreferrer">voir dans GitLab</a></>}
          {onConfigure && <> — <button className="commit-tooltip-ci-link" onClick={onConfigure}>Configurer</button></>}
        </div>
      )}
      {play.isSuccess && (
        <div className="commit-tooltip-ci-note">Job « {play.variables.name} » lancé (statut : {play.data.status}).</div>
      )}
      {failure && (
        <div className="commit-tooltip-ci-error" role="alert">
          {failure.message}
          {failure.jobUrl && <> <a href={failure.jobUrl} target="_blank" rel="noreferrer">Ouvrir le job dans GitLab</a></>}
        </div>
      )}
    </div>
  );
}

export function CommitCiSection({
  repoId, sha, pinned, onPin, onConfigure,
}: {
  repoId: string;
  sha: string;
  pinned: boolean;
  // Épingle la tooltip (clic sur « Lancer »).
  onPin: () => void;
  // Ouvre la modal « Configuration CI » ; absent = pas de lien « Configurer ».
  onConfigure?: () => void;
}) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setArmed(true), CI_HOVER_DELAY_MS);
    return () => window.clearTimeout(t);
  }, []);

  const enabled = armed || pinned;
  const query = useQuery({
    queryKey: ['commit-ci', repoId, sha],
    queryFn: () => api.commitCi(repoId, sha),
    enabled,
    staleTime: 60_000,
    // Section secondaire : un échec (403, GitLab injoignable) ne doit pas retenter en boucle sous le curseur.
    retry: false,
  });

  if (!enabled) return null;
  const withArtifacts = query.data?.pipelines.filter((p) => p.artifacts.length > 0) ?? [];
  const packages = query.data?.packages ?? [];
  const images = query.data?.images ?? [];
  const hasManual = (query.data?.pipelines ?? []).some((p) => p.manualJobs.length > 0);
  const hasAny = withArtifacts.length > 0 || packages.length > 0 || images.length > 0 || hasManual;
  return (
    <div className="commit-tooltip-ci">
      <div className="commit-tooltip-ci-title">CI / artefacts</div>
      {query.isLoading && <div className="commit-tooltip-ci-note">Chargement…</div>}
      {query.isError && <div className="commit-tooltip-ci-note">Artefacts CI indisponibles.</div>}
      {query.data && !hasAny && (
        <div className="commit-tooltip-ci-note">
          {query.data.pipelines.length === 0 ? 'Aucun pipeline pour ce commit.' : 'Aucun artefact, package, image ni job manuel.'}
        </div>
      )}
      {withArtifacts.map((p) => <PipelineBlock key={p.id} pipeline={p} />)}
      {packages.length > 0 && (
        <div className="commit-tooltip-ci-pipeline">
          <div className="commit-tooltip-ci-group">Packages</div>
          <ul>{packages.map((pkg) => <PackageRow key={pkg.id} pkg={pkg} />)}</ul>
        </div>
      )}
      {images.length > 0 && (
        <div className="commit-tooltip-ci-pipeline">
          <div className="commit-tooltip-ci-group">Images</div>
          <ul>{images.map((img) => <ImageRow key={`${img.repository}:${img.tag}`} image={img} />)}</ul>
        </div>
      )}
      {query.data && (
        <ManualJobs repoId={repoId} pipelines={query.data.pipelines} onPin={onPin} onConfigure={onConfigure} />
      )}
      {query.data?.pipelinesTruncated && (
        <div className="commit-tooltip-ci-note">Seuls les pipelines les plus récents de ce commit sont affichés.</div>
      )}
      {query.data?.packagesTruncated && (
        <div className="commit-tooltip-ci-note">Packages non affichés : le dépôt en compte trop pour être parcouru.</div>
      )}
    </div>
  );
}
